/**
 * Core Progress Component
 *
 * Determinate/indeterminate progress bar and circular variant.
 * Used for upload progress, step completion, quotas, etc.
 *
 * @example
 *   <Progress value={65} label="Upload progress" showValue />
 *   <Progress variant="circular" value={75} size={48} showValue />
 *   <Progress indeterminate label="Processing..." />
 */
import React from 'react';
import { LinearProgress, CircularProgress, Typography, Box } from '@mui/material';
import { cn } from '@/lib/utils';

export interface ProgressProps {
  /** Progress value (0-100) */
  value?: number;
  /** Indeterminate mode (no value) */
  indeterminate?: boolean;
  /** Bar or circle */
  variant?: 'linear' | 'circular';
  /** Color */
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  /** Label text */
  label?: string;
  /** Show value percentage */
  showValue?: boolean;
  /** Size for circular variant */
  size?: number;
  /** Height for linear variant */
  height?: number;
  /** Custom color based on value threshold */
  autoColor?: boolean;
  className?: string;
}

const getAutoColor = (value: number): 'success' | 'warning' | 'error' | 'primary' => {
  if (value >= 90) return 'error';
  if (value >= 70) return 'warning';
  if (value >= 50) return 'primary';
  return 'success';
};

export const Progress: React.FC<ProgressProps> = ({
  value = 0,
  indeterminate = false,
  variant = 'linear',
  color = 'primary',
  label,
  showValue = false,
  size = 40,
  height = 6,
  autoColor = false,
  className = '',
}) => {
  const effectiveColor = autoColor ? getAutoColor(value) : color;

  if (variant === 'circular') {
    return (
      <Box className={cn('inline-flex flex-col items-center gap-1', className)}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <CircularProgress
            variant={indeterminate ? 'indeterminate' : 'determinate'}
            value={value}
            size={size}
            color={effectiveColor}
            thickness={4}
          />
          {showValue && !indeterminate && (
            <Box sx={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Typography variant="caption" fontWeight={700} fontSize={size * 0.22} color="text.secondary">
                {Math.round(value)}%
              </Typography>
            </Box>
          )}
        </Box>
        {label && <Typography variant="caption" color="text.secondary">{label}</Typography>}
      </Box>
    );
  }

  return (
    <Box className={cn('w-full', className)}>
      {(label || showValue) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          {label && <Typography variant="caption" fontWeight={500} color="text.secondary">{label}</Typography>}
          {showValue && !indeterminate && (
            <Typography variant="caption" fontWeight={700} color="text.secondary">{Math.round(value)}%</Typography>
          )}
        </Box>
      )}
      <LinearProgress
        variant={indeterminate ? 'indeterminate' : 'determinate'}
        value={value}
        color={effectiveColor}
        sx={{ height, borderRadius: height / 2, '& .MuiLinearProgress-bar': { borderRadius: height / 2 } }}
      />
    </Box>
  );
};
export default Progress;
